import { STR } from '../i18n';
import { useAppearance } from '../lib/theme';

/**
 * R1 — THE appearance control. Palette and mode are independent, so they are two controls,
 * never one four-way switch. Both write a client-side preference that overrides the
 * configured default; neither goes near the server.
 *
 * ONE TREATMENT, EVERYWHERE. This used to be three: the admin rail's stacked state buttons,
 * a segmented pair in the sign-in page's fixed corner, and a third segmented pair inside the
 * launcher's account menu. Three drawings of one control is three things to keep in step, and
 * they had already diverged in size and wording. The rail's treatment is now the only one —
 * two stacked buttons, each showing the CURRENT value of its own axis and cycling it. Every
 * surface that offers appearance renders THIS, and the only thing allowed to differ is the
 * furniture around it (the rail column, the sign-in corner, the account-menu section): never
 * the buttons' size, padding, font, radius, labels or behaviour.
 *
 * It stays two-state. There is deliberately no "system"/"auto": the resolved default already
 * comes from branding (see `lib/theme.ts`), so a third state would be a second home for the
 * same fact.
 *
 * BOTH BUTTONS ARE ALWAYS DRAWN IN THE SELECTED TREATMENT — `.seg-value` in components.css,
 * the look `.seg[aria-pressed="true"]` gives the rail. Neither button is one option of a
 * pair: each DISPLAYS the current value of its axis, so there is no unselected form of it to
 * draw. Styling on `aria-pressed` made the resting state a property of WHICH value was in
 * force — in Default/light both buttons came out as bare unbordered words that read as no
 * control at all, and nothing told somebody who had not clicked yet what was applied.
 *
 * Which is also why neither carries `aria-pressed` any more. A button that always looks
 * pressed and reports a toggling pressed state announces one thing and shows another; these
 * are not toggles. The state is instead in the accessible name, which carries the axis AND
 * its current value (`STR.appearance.paletteLabel` / `modeLabel`) so what this round made
 * visible is conveyed non-visually too. The Grants view switch keeps `aria-pressed`: that one
 * IS a genuine selected/unselected pair, which is why the resting treatment had to be scoped
 * to a class of this control's own rather than given to `.seg` outright.
 *
 * A DELIBERATE, RECORDED DIVERGENCE from the frozen launcher and sign-in demos, which draw
 * segmented pills here. Nothing else on those surfaces diverges from them.
 *
 * A FOLLOW-ON RULING, on top of D38: inside a BOUNDED container — today, only the launcher's
 * account-menu popover — the pair FILLS the container edge to edge instead of sitting at the
 * rail's fixed width, because floating at roughly half the menu's width read as smaller than
 * the segmented pills it replaced. `fill` is the one dimension surrounding furniture is now
 * permitted to set; every other property this file draws — chrome, wording, the `.seg-value`
 * resting treatment, the absent `aria-pressed`, the accessible name's axis-plus-value content
 * — is unchanged and not something a caller can override. `fill` defaults `false`, so every
 * existing caller (the rail, the sign-in corner) renders exactly as it did before — the rail
 * cannot change by omission, and neither can the corner, which has no bounded width to fill.
 */

/**
 * 78px — the width these buttons already rendered at inside the 96px rail (96 minus its
 * 9px side padding, twice), when they were `width: 100%`. Pinned as a number rather than a
 * percentage BECAUSE identical means identical: a percentage would resolve differently in the
 * rail, the 264px menu popover and the fixed corner. The value is the rail's so that the rail
 * — the treatment everything else was aligned TO — renders exactly as it did before.
 */
const BUTTON_WIDTH = 78;

/** Everything BOTH modes share. `width` is the one property that varies — see `segStyle` /
 *  `segStyleFill` below — so it is deliberately absent here rather than overridden per mode. */
const segStyleBase = {
  padding: '6px 2px',
  borderRadius: 8,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '.03em',
  cursor: 'pointer',
} as const;

/** The rail and the sign-in corner: pinned to the rail's own width, unchanged by this ruling. */
const segStyle = { ...segStyleBase, width: BUTTON_WIDTH } as const;

/**
 * Ruling 2 — no `width` at all, deliberately, and NOT a second hardcoded number. The caller
 * is responsible for a container that resolves each button's width itself (equal flex
 * children, or — as the account menu does — a two-equal-column grid, whose default
 * `justify-items: stretch` fills each column because nothing here fixes an inline size to
 * fight it). This is a container concern, not a new constant this component owns.
 */
const segStyleFill = segStyleBase;

export function AppearanceControl({ fill = false }: { fill?: boolean } = {}) {
  const { palette, mode, setPalette, setMode } = useAppearance();

  /* Both halves of each button — the printed short value and the spoken long one — come off
     the SAME resolved value, so the chip and the accessible name can never disagree. */
  const sage = palette === 'sage';
  const dark = mode === 'dark';
  const style = fill ? segStyleFill : segStyle;

  return (
    <>
      <button
        type="button"
        className="seg seg-value"
        aria-label={STR.appearance.paletteLabel(
          sage ? STR.appearance.paletteNameSage : STR.appearance.paletteNameDefault,
        )}
        onClick={() => {
          setPalette(sage ? 'default' : 'sage');
        }}
        style={style}
      >
        {sage ? STR.appearance.paletteShortSage : STR.appearance.paletteShortDefault}
      </button>
      <button
        type="button"
        className="seg seg-value"
        aria-label={STR.appearance.modeLabel(
          dark ? STR.appearance.modeNameDark : STR.appearance.modeNameLight,
        )}
        onClick={() => {
          setMode(dark ? 'light' : 'dark');
        }}
        style={style}
      >
        {dark ? STR.appearance.modeShortDark : STR.appearance.modeShortLight}
      </button>
    </>
  );
}
